import * as React from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import type { KitchenArrivalRow } from "./RallyBoardPopup";
import type {
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";
import type { FirefightClip } from "./GameStory_firefights";
import type { PounceWindowRow } from "../../shared/types/pounceEtc";

import { CondensedRallyTimeline_ContextOnly } from "./CondensedRallyTimeline_ContextOnly";
import { ActiveRallyVideo } from "./ActiveRallyVideo";

import {
  buildRallyCausalitySummaryMap,
  getCausalityOverlayText,
  type RallyCausalitySummary,
  type RallyCausalityShotRow,
} from "./GameStory_RallyCausality_Helpers";
import { CvJson } from "../../shared/types/cvJson";

type PlayerId = 0 | 1 | 2 | 3;
type Team01 = 0 | 1;
type TernaryFilter = "all" | "yes" | "no";

function isPlayerId(v: unknown): v is PlayerId {
  return v === 0 || v === 1 || v === 2 || v === 3;
}

function teamOfPlayer(playerId: PlayerId): Team01 {
  return playerId === 0 || playerId === 1 ? 0 : 1;
}

function getWinnerTeam(row: KitchenArrivalRow | null): Team01 | null {
  const v = row?.winnerTeam;
  return v === 0 || v === 1 ? v : null;
}

function getPointAwarded(row: KitchenArrivalRow | null): boolean | null {
  if (typeof row?.scoredPoint === "boolean") return row.scoredPoint;
  return null;
}

function getNumShots(row: KitchenArrivalRow | null): number {
  if (typeof row?.numShots === "number" && Number.isFinite(row.numShots)) {
    return row.numShots;
  }
  if (Array.isArray(row?.shotNumberOverlays)) {
    return row.shotNumberOverlays.length;
  }
  return 0;
}

function rowIncludesPlayer(
  row: KitchenArrivalRow | null,
  playerId: PlayerId,
): boolean {
  if (!row || !Array.isArray(row.shotNumberOverlays)) return false;

  return row.shotNumberOverlays.some((o) => {
    const pid = o?.playerId ?? o?.rawShot?.player_id;
    return pid === playerId;
  });
}

function FilterPillButton(props: {
  label: React.ReactNode;
  selected: boolean;
  onClick: () => void;
  bgSelected?: string;
}) {
  const { label, selected, onClick, bgSelected = "rgba(0,0,0,0.08)" } = props;

  return (
    <Button
      size="small"
      variant="text"
      onClick={onClick}
      sx={{
        textTransform: "none",
        minHeight: 24,
        py: 0,
        px: 1,
        minWidth: 0,
        fontWeight: 900,
        fontSize: 12,
        borderRadius: "999px",
        border: selected
          ? "1px solid rgba(0,0,0,0.20)"
          : "1px solid transparent",
        bgcolor: selected ? bgSelected : "white",
        boxShadow: selected ? "inset 0 0 0 1px rgba(0,0,0,0.12)" : "none",
        "&:hover": {
          borderColor: "rgba(0,0,0,0.16)",
          bgcolor: selected ? bgSelected : "rgba(0,0,0,0.03)",
        },
      }}
    >
      {label}
    </Button>
  );
}

function formatOverlayTag(kind: RallyCausalityShotRow["overlayKind"]): string {
  switch (kind) {
    case "setup":
      return "SETUP";
    case "shift":
      return "SHIFT";
    case "no_return":
      return "NO RETURN";
    case "finish":
      return "FINISH";
    case "warning":
      return "WARNING";
    default:
      return "";
  }
}

function shotBadgeBg(kind: RallyCausalityShotRow["overlayKind"]): string {
  switch (kind) {
    case "setup":
      return "rgba(120,160,255,0.18)";
    case "shift":
      return "rgba(255,190,90,0.20)";
    case "no_return":
      return "rgba(255,110,110,0.22)";
    case "finish":
      return "rgba(120,225,160,0.22)";
    case "warning":
      return "rgba(255,140,90,0.18)";
    default:
      return "rgba(0,0,0,0.05)";
  }
}

function shotBadgeBorder(kind: RallyCausalityShotRow["overlayKind"]): string {
  switch (kind) {
    case "setup":
      return "rgba(120,160,255,0.48)";
    case "shift":
      return "rgba(255,190,90,0.52)";
    case "no_return":
      return "rgba(255,110,110,0.56)";
    case "finish":
      return "rgba(120,225,160,0.50)";
    case "warning":
      return "rgba(255,140,90,0.44)";
    default:
      return "rgba(0,0,0,0.14)";
  }
}

function deltaText(v: number | null | undefined): string {
  if (typeof v !== "number" || !Number.isFinite(v)) return "—";
  const s = v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2);
  return s;
}

function CausalityMiniSummary(props: { summary: RallyCausalitySummary }) {
  const { summary } = props;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.35,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 0.45,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {summary.setupShotNumber != null && (
          <Box
            sx={{
              px: 0.65,
              py: 0.15,
              borderRadius: "999px",
              fontSize: 10.5,
              fontWeight: 900,
              bgcolor: shotBadgeBg("setup"),
              border: `1px solid ${shotBadgeBorder("setup")}`,
              whiteSpace: "nowrap",
            }}
          >
            Setup {summary.setupShotNumber}
          </Box>
        )}

        {summary.noReturnShotNumber != null && (
          <Box
            sx={{
              px: 0.65,
              py: 0.15,
              borderRadius: "999px",
              fontSize: 10.5,
              fontWeight: 900,
              bgcolor: shotBadgeBg("no_return"),
              border: `1px solid ${shotBadgeBorder("no_return")}`,
              whiteSpace: "nowrap",
            }}
          >
            No return {summary.noReturnShotNumber}
          </Box>
        )}

        {summary.finishShotNumber != null && (
          <Box
            sx={{
              px: 0.65,
              py: 0.15,
              borderRadius: "999px",
              fontSize: 10.5,
              fontWeight: 900,
              bgcolor: shotBadgeBg("finish"),
              border: `1px solid ${shotBadgeBorder("finish")}`,
              whiteSpace: "nowrap",
            }}
          >
            Finish {summary.finishShotNumber}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          fontSize: 11,
          lineHeight: 1.2,
          color: "rgba(0,0,0,0.70)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {summary.summaryShort}
      </Box>
    </Box>
  );
}

function RallyCausalityStrip(props: {
  summary: RallyCausalitySummary | null;
  renderPlayerIdentity: RenderPlayerIdentity;
  currentShotNumber: number | null;
  selectedPlayerId: PlayerId | null;
  height?: number;
}) {
  const {
    summary,
    renderPlayerIdentity,
    currentShotNumber,
    selectedPlayerId,
    height = 82,
  } = props;

  if (!summary) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          color: "rgba(0,0,0,0.52)",
        }}
      >
        No causality strip available.
      </Box>
    );
  }

  const shotRows = summary.shotRows;

  return (
    <Box
      sx={{
        height,
        px: 1,
        py: 0.8,
        display: "flex",
        alignItems: "stretch",
        gap: 0.55,
        overflowX: "auto",
        overflowY: "hidden",
        bgcolor: "rgba(0,0,0,0.012)",
      }}
    >
      {shotRows.map((sr) => {
        const isCurrent = currentShotNumber === sr.shotNumber;
        const isSelectedPlayer =
          selectedPlayerId != null && sr.hitterPlayerId === selectedPlayerId;

        return (
          <Box
            key={sr.shotNumber}
            sx={{
              minWidth: 64,
              width: 64,
              borderRadius: 1.5,
              px: 0.45,
              py: 0.35,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
              border: isCurrent
                ? "2px solid rgba(0,0,0,0.70)"
                : `1px solid ${shotBadgeBorder(sr.overlayKind)}`,
              bgcolor: shotBadgeBg(sr.overlayKind),
              boxShadow: isCurrent
                ? "inset 0 0 0 1px rgba(255,255,255,0.30)"
                : "none",
            }}
          >
            <Box
              sx={{
                fontSize: 11,
                fontWeight: 900,
                lineHeight: 1,
              }}
            >
              {sr.shotNumber}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 28,
                borderRadius: 1,
                outline: isSelectedPlayer
                  ? "2px solid rgba(0,0,0,0.42)"
                  : "none",
                outlineOffset: "1px",
              }}
            >
              {sr.hitterPlayerId != null
                ? renderPlayerIdentity({
                    playerId: sr.hitterPlayerId,
                    labelOnly: false,
                    size: 28,
                    borderRadius: 4,
                    fontSize: 10,
                  })
                : null}
            </Box>

            <Box
              sx={{
                fontSize: 9.5,
                fontWeight: 900,
                lineHeight: 1,
                color: "rgba(0,0,0,0.78)",
                whiteSpace: "nowrap",
              }}
            >
              {formatOverlayTag(sr.overlayKind) || "—"}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function ShotRowsTable(props: {
  summary: RallyCausalitySummary | null;
  currentShotNumber: number | null;
}) {
  const { summary, currentShotNumber } = props;

  if (!summary) return null;

  return (
    <Box
      sx={{
        borderTop: "1px solid rgba(0,0,0,0.08)",
        maxHeight: 220,
        overflow: "auto",
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={{
          "& .MuiTableCell-root": {
            verticalAlign: "top",
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 900, width: 48 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 900, width: 90 }}>Kind</TableCell>
            <TableCell sx={{ fontWeight: 900, width: 92 }}>Phase</TableCell>
            <TableCell sx={{ fontWeight: 900, width: 70 }} align="right">
              Swing
            </TableCell>
            <TableCell sx={{ fontWeight: 900, width: 74 }} align="right">
              Causal
            </TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Why</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {summary.shotRows.map((sr) => {
            const isCurrent = currentShotNumber === sr.shotNumber;

            return (
              <TableRow
                key={sr.shotNumber}
                sx={{
                  bgcolor: isCurrent ? "rgba(0,0,0,0.055)" : undefined,
                }}
              >
                <TableCell sx={{ fontWeight: 900 }}>{sr.shotNumber}</TableCell>
                <TableCell>
                  <Box
                    sx={{
                      display: "inline-flex",
                      px: 0.7,
                      py: 0.18,
                      borderRadius: "999px",
                      fontSize: 10.5,
                      fontWeight: 900,
                      bgcolor: shotBadgeBg(sr.overlayKind),
                      border: `1px solid ${shotBadgeBorder(sr.overlayKind)}`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatOverlayTag(sr.overlayKind) || "—"}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontSize: 11.2, fontWeight: 800 }}>
                  {sr.phaseLabel}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {deltaText(sr.advantageDeltaAgainstHitterTeam)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {sr.causalScore.toFixed(2)}
                </TableCell>
                <TableCell>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}
                  >
                    <Box sx={{ fontSize: 11.2, lineHeight: 1.2 }}>
                      {sr.explanationShort}
                    </Box>
                    {!!sr.evidence.length && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.35,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        {sr.evidence.slice(0, 3).map((ev, idx) => (
                          <Box
                            key={`${sr.shotNumber}-${ev.kind}-${idx}`}
                            title={`${ev.source}: ${ev.detail}`}
                            sx={{
                              px: 0.55,
                              py: 0.12,
                              borderRadius: "999px",
                              border: "1px solid rgba(0,0,0,0.12)",
                              bgcolor:
                                ev.polarity === "blame"
                                  ? "rgba(210,70,70,0.10)"
                                  : ev.polarity === "credit"
                                    ? "rgba(40,145,80,0.10)"
                                    : "rgba(0,0,0,0.04)",
                              color:
                                ev.polarity === "blame"
                                  ? "rgba(145,45,45,0.95)"
                                  : ev.polarity === "credit"
                                    ? "rgba(25,110,55,0.95)"
                                    : "rgba(0,0,0,0.66)",
                              fontSize: 9.8,
                              fontWeight: 900,
                              lineHeight: 1.2,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {ev.label}
                          </Box>
                        ))}
                      </Box>
                    )}
                    {!!sr.leverageTags.length && (
                      <Box
                        sx={{
                          fontSize: 10.2,
                          color: "rgba(0,0,0,0.56)",
                          lineHeight: 1.15,
                        }}
                      >
                        {sr.leverageTags.join(" • ")}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

export function RallyCausalityOverviewPopup(props: {
  open: boolean;
  onClose: () => void;
  packageId: string;
  rows: KitchenArrivalRow[];
  activeRallyIndex: number | null;
  renderPlayerIdentity: RenderPlayerIdentity;
  insightsToCvPlayerIndex: PlayerIndexMap;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
  pounceWindowRows: PounceWindowRow[];
    cvJson: CvJson | null;
  
}) {
  const {
    open,
    onClose,
    packageId,
    rows,
    activeRallyIndex,
    renderPlayerIdentity,
    insightsToCvPlayerIndex,
    firefightsClips,
    firefightRallySet,
    pounceWindowRows,
    cvJson,
  } = props;

  const [currentShotNumber, setCurrentShotNumber] = React.useState<
    number | null
  >(null);

  const [selectedPlayerId, setSelectedPlayerId] =
    React.useState<PlayerId | null>(null);
  const [wonFilter, setWonFilter] = React.useState<TernaryFilter>("all");
  const [pointAwardedFilter, setPointAwardedFilter] =
    React.useState<TernaryFilter>("all");

  const causalitySummaryMap = React.useMemo(
    () => buildRallyCausalitySummaryMap({ rows, pounceWindowRows, cvJson }),
    [rows, pounceWindowRows, cvJson],
  );

  const selectableRows = React.useMemo(
    () =>
      rows.filter((row) => {
        const s = causalitySummaryMap.get(row.rallyIndex);
        return !!s && s.shotRows.length > 0;
      }),
    [rows, causalitySummaryMap],
  );

  const filteredRows = React.useMemo(() => {
    return selectableRows.filter((row) => {
      if (
        selectedPlayerId != null &&
        !rowIncludesPlayer(row, selectedPlayerId)
      ) {
        return false;
      }

      if (wonFilter !== "all") {
        const winner = getWinnerTeam(row);
        const selectedTeam =
          selectedPlayerId != null ? teamOfPlayer(selectedPlayerId) : null;

        if (selectedTeam == null || winner == null) return false;

        const didWin = winner === selectedTeam;
        if (wonFilter === "yes" && !didWin) return false;
        if (wonFilter === "no" && didWin) return false;
      }

      if (pointAwardedFilter !== "all") {
        const pointAwarded = getPointAwarded(row);
        if (pointAwarded == null) return false;
        if (pointAwardedFilter === "yes" && !pointAwarded) return false;
        if (pointAwardedFilter === "no" && pointAwarded) return false;
      }

      return true;
    });
  }, [selectableRows, selectedPlayerId, wonFilter, pointAwardedFilter]);

  const [selectedRallyIndex, setSelectedRallyIndex] = React.useState<
    number | null
  >(null);

  React.useEffect(() => {
    if (!open) return;

    const defaultRallyIndex =
      activeRallyIndex != null &&
      filteredRows.some((r) => r.rallyIndex === activeRallyIndex)
        ? activeRallyIndex
        : (filteredRows[0]?.rallyIndex ?? null);

    setSelectedRallyIndex(defaultRallyIndex);
  }, [open, activeRallyIndex, filteredRows]);

  React.useEffect(() => {
    if (selectedRallyIndex == null) return;
    if (!filteredRows.some((r) => r.rallyIndex === selectedRallyIndex)) {
      setSelectedRallyIndex(filteredRows[0]?.rallyIndex ?? null);
    }
  }, [filteredRows, selectedRallyIndex]);

  const selectedRow = React.useMemo(
    () => rows.find((r) => r.rallyIndex === selectedRallyIndex) ?? null,
    [rows, selectedRallyIndex],
  );

  const selectedSummary = React.useMemo(
    () =>
      selectedRallyIndex != null
        ? (causalitySummaryMap.get(selectedRallyIndex) ?? null)
        : null,
    [selectedRallyIndex, causalitySummaryMap],
  );

  const [pos, setPos] = React.useState({ x: 26, y: 90 });
  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
    dragging: boolean;
  } | null>(null);

  const onMouseDownHeader = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: pos.x,
      startPosY: pos.y,
      dragging: true,
    };
    e.preventDefault();
  };

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d?.dragging) return;

      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;

      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 240, d.startPosX + dx)),
        y: Math.max(0, Math.min(window.innerHeight - 140, d.startPosY + dy)),
      });
    };

    const onUp = () => {
      if (dragRef.current) dragRef.current.dragging = false;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  React.useEffect(() => {
    setCurrentShotNumber(null);
  }, [selectedRow?.rallyIndex]);

  if (!open) return null;

  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: 1450,
        maxWidth: "calc(100vw - 36px)",
        height: "95vh",
        maxHeight: "calc(100vh - 120px)",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 2000,
        border: "1px solid rgba(0,0,0,0.14)",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
        onMouseDown={onMouseDownHeader}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ fontWeight: 900 }}>Rally Causality</Box>
          <Box sx={{ fontSize: 12, opacity: 0.7 }}>
            {filteredRows.length} rallies
          </Box>
        </Box>

        <Button
          size="small"
          variant="text"
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={{ textTransform: "none", fontWeight: 900 }}
        >
          Close
        </Button>
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          borderBottom: "1px solid rgba(0,0,0,0.10)",
          bgcolor: "rgba(0,0,0,0.025)",
          fontSize: 12.5,
          lineHeight: 1.35,
          color: "rgba(0,0,0,0.68)",
        }}
      >
        <Box sx={{ fontWeight: 900, color: "rgba(0,0,0,0.86)", mb: 0.25 }}>
          Experiment note
        </Box>
        The purpose of this experiment was to make blame and credit more rigorous than a single terminal-shot label by showing the chain of events that shaped a rally. The promising part is that it can expose setup pressure and recovery moments; where it stalled is that deterministic causality can get noisy fast, especially when a rally turns on several small choices rather than one clean mistake.
      </Box>

      <Box
        sx={{
          px: 1.2,
          py: 0.8,
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          flexWrap: "wrap",
          borderBottom: "1px solid rgba(0,0,0,0.10)",
          bgcolor: "rgba(0,0,0,0.012)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.45,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 800,
              color: "rgba(0,0,0,0.60)",
              mr: 0.2,
            }}
          >
            Player
          </Box>

          <FilterPillButton
            label="All"
            selected={selectedPlayerId == null}
            onClick={() => {
              setSelectedPlayerId(null);
              setWonFilter("all");
            }}
          />

          {([0, 1, 2, 3] as PlayerId[]).map((pid) => {
            const team = teamOfPlayer(pid);
            const selected = selectedPlayerId === pid;

            return (
              <Button
                key={pid}
                size="small"
                variant="text"
                onClick={() => setSelectedPlayerId(pid)}
                sx={{
                  textTransform: "none",
                  minHeight: 24,
                  py: 0,
                  px: 0.55,
                  minWidth: 0,
                  borderRadius: "999px",
                  border: selected
                    ? `1px solid ${
                        team === 0
                          ? "rgba(95,165,255,0.55)"
                          : "rgba(120,225,160,0.55)"
                      }`
                    : "1px solid transparent",
                  bgcolor: selected
                    ? team === 0
                      ? "rgba(95,165,255,0.12)"
                      : "rgba(120,225,160,0.12)"
                    : "white",
                  boxShadow: selected
                    ? "inset 0 0 0 1px rgba(0,0,0,0.05)"
                    : "none",
                  "&:hover": {
                    borderColor:
                      team === 0
                        ? "rgba(95,165,255,0.45)"
                        : "rgba(120,225,160,0.45)",
                    bgcolor: selected
                      ? team === 0
                        ? "rgba(95,165,255,0.16)"
                        : "rgba(120,225,160,0.16)"
                      : "rgba(0,0,0,0.03)",
                  },
                }}
              >
                {renderPlayerIdentity({
                  playerId: pid,
                  labelOnly: false,
                  size: 34,
                  borderRadius: 5,
                  fontSize: 12,
                })}
              </Button>
            );
          })}
        </Box>

        <Box
          sx={{
            display: "flex",
            marginLeft: "12px",
            alignItems: "center",
            gap: 0.45,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 800,
              color: "rgba(0,0,0,0.60)",
              mr: 0.2,
            }}
          >
            Won rally
          </Box>

          <FilterPillButton
            label="All"
            selected={wonFilter === "all"}
            onClick={() => setWonFilter("all")}
          />
          <FilterPillButton
            label="Yes"
            selected={wonFilter === "yes"}
            onClick={() => setWonFilter("yes")}
          />
          <FilterPillButton
            label="No"
            selected={wonFilter === "no"}
            onClick={() => setWonFilter("no")}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.45,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 800,
              color: "rgba(0,0,0,0.60)",
              mr: 0.2,
            }}
          >
            Point awarded
          </Box>

          <FilterPillButton
            label="All"
            selected={pointAwardedFilter === "all"}
            onClick={() => setPointAwardedFilter("all")}
          />
          <FilterPillButton
            label="Yes"
            selected={pointAwardedFilter === "yes"}
            onClick={() => setPointAwardedFilter("yes")}
          />
          <FilterPillButton
            label="No"
            selected={pointAwardedFilter === "no"}
            onClick={() => setPointAwardedFilter("no")}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            width: 520,
            maxWidth: "50%",
            borderRight: "1px solid rgba(0,0,0,0.12)",
            overflow: "auto",
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              "& .MuiTableCell-root": { verticalAlign: "top" },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 900, width: 62 }}>Rally</TableCell>
                <TableCell sx={{ fontWeight: 900, width: 62 }} align="right">
                  Shots
                </TableCell>
                <TableCell sx={{ fontWeight: 900, width: 74 }} align="center">
                  Won Rally
                </TableCell>
                <TableCell sx={{ fontWeight: 900, width: 74 }} align="center">
                  Point Awarded
                </TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Why it turned</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => {
                const isSelected = row.rallyIndex === selectedRallyIndex;
                const winner = getWinnerTeam(row);
                const pointAwarded = getPointAwarded(row);
                const selectedTeam =
                  selectedPlayerId != null
                    ? teamOfPlayer(selectedPlayerId)
                    : null;
                const didSelectedPlayerWin =
                  selectedTeam != null && winner != null
                    ? winner === selectedTeam
                    : null;

                const summary = causalitySummaryMap.get(row.rallyIndex) ?? null;

                return (
                  <TableRow
                    key={row.rallyIndex}
                    hover
                    onClick={() => setSelectedRallyIndex(row.rallyIndex)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: isSelected ? "rgba(0,0,0,0.06)" : undefined,
                    }}
                  >
                    <TableCell sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>
                      {row.rallyIndex + 1}
                    </TableCell>

                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {getNumShots(row)}
                    </TableCell>

                    <TableCell align="center">
                      {didSelectedPlayerWin == null ? (
                        winner == null ? (
                          <Box
                            sx={{
                              fontSize: 11,
                              fontWeight: 900,
                              color: "rgba(0,0,0,0.55)",
                            }}
                          >
                            —
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              px: 0.85,
                              py: 0.2,
                              borderRadius: "999px",
                              fontSize: 10.5,
                              fontWeight: 800,
                              lineHeight: 1,
                              color:
                                winner === 0
                                  ? "rgba(35,90,180,0.96)"
                                  : "rgba(28,120,62,0.96)",
                              bgcolor:
                                winner === 0
                                  ? "rgba(95,165,255,0.16)"
                                  : "rgba(120,225,160,0.16)",
                              border: `1px solid ${
                                winner === 0
                                  ? "rgba(95,165,255,0.42)"
                                  : "rgba(120,225,160,0.42)"
                              }`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Team {winner}
                          </Box>
                        )
                      ) : (
                        <Box
                          sx={{
                            fontSize: 11,
                            fontWeight: 900,
                            color: didSelectedPlayerWin
                              ? "rgba(20,120,55,0.95)"
                              : "rgba(190,60,60,0.95)",
                          }}
                        >
                          {didSelectedPlayerWin ? "Yes" : "No"}
                        </Box>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Box
                        sx={{
                          fontSize: 11,
                          fontWeight: 900,
                          color:
                            pointAwarded == null
                              ? "rgba(0,0,0,0.55)"
                              : pointAwarded
                                ? "rgba(20,120,55,0.95)"
                                : "rgba(0,0,0,0.55)",
                        }}
                      >
                        {pointAwarded == null
                          ? "—"
                          : pointAwarded
                            ? "Yes"
                            : "No"}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ minWidth: 0, width: "100%" }}>
                      {summary ? (
                        <CausalityMiniSummary summary={summary} />
                      ) : (
                        <Box sx={{ fontSize: 11, color: "rgba(0,0,0,0.52)" }}>
                          —
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 1, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
            {selectedRow && selectedSummary ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                <Box sx={{ fontWeight: 900 }}>
                  Rally {selectedRow.rallyIndex + 1}
                  {" • "}
                  {getNumShots(selectedRow)} shots
                </Box>
                <Box sx={{ fontSize: 12, color: "rgba(0,0,0,0.72)" }}>
                  {selectedSummary.summaryLong}
                </Box>
              </Box>
            ) : (
              <Box sx={{ opacity: 0.7 }}>No rally selected.</Box>
            )}
          </Box>

          {/* <Box>
            <CondensedRallyTimeline_ContextOnly
              activeRally={selectedRow}
              rows={rows}
              barsAreaHeightPx={40}
              showLegend={false}
              showRallyIndex={true}
              showWinnerAndServe={true}
              firefightRallySet={firefightRallySet}
            />
          </Box> */}

          <Box sx={{ borderBottom: "1px solid rgba(0,0,0,0.10)" }}>
            <RallyCausalityStrip
              summary={selectedSummary}
              renderPlayerIdentity={renderPlayerIdentity}
              currentShotNumber={currentShotNumber}
              selectedPlayerId={selectedPlayerId}
              height={82}
            />
          </Box>

          <ShotRowsTable
            summary={selectedSummary}
            currentShotNumber={currentShotNumber}
          />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              bgcolor: "black",
            }}
          >
            {selectedRow ? (
              <ActiveRallyVideo
                aiContextLabel="Rally Causality"
                focusedFilter={null}
                packageId={packageId}
                row={selectedRow}
                rows={rows}
                activeShotNumber={null}
                activeShotExtraText={undefined}
                // shotOverlayPersistentExtraText={(shotNumber: number | null) =>
                //   getCausalityOverlayText({
                //     summary: selectedSummary,
                //     shotNumber,
                //   })
                // }
                shotOverlayPersistentExtraText={(overlay) =>
                  getCausalityOverlayText({
                    summary: selectedSummary,
                    shotNumber:
                      typeof overlay?.shotNumber === "number"
                        ? overlay.shotNumber
                        : null,
                  })
                }
                showCondensedTimeline={false}
                fontColor="white"
                controlsPlacement="left"
                firefightsClips={firefightsClips}
                firefightRallySet={firefightRallySet}
                showRateControls={true}
                showTitle={false}
                showScoreEtc={false}
                showAi={true}
                aiDrawerMode="split"
                showLoopScrubber={true}
                defaultLoopUiOpen={false}
                showPopoutButton={true}
                isInFullPopup={false}
                renderPlayerIdentity={renderPlayerIdentity}
                insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                onPlaybackShotNumberChange={(shotNumber: number | null) => {
                  setCurrentShotNumber(shotNumber);
                }}
              />
            ) : (
              <Box sx={{ p: 2, color: "rgba(255,255,255,0.75)" }}>
                No rally selected.
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
